import { useEffect, useState } from 'react';
import { isRestComplete, restProgress, restRemainingSeconds, type RestTimer } from '@liftx/core';
import type { ActiveHoldTimer } from '../../data/types';

export interface HoldTimerDisplay {
  remainingSeconds: number;
  progress: number;
  isComplete: boolean;
}

const IDLE: HoldTimerDisplay = { remainingSeconds: 0, progress: 1, isComplete: true };

/**
 * Live display for a persisted Hold-exercise countdown (Plank / Hold Exercise
 * Countdown Timer feature) — a near-identical sibling of `useRestTimer`, not
 * a generalisation of it: both are thin wrappers that supply the current
 * instant to `@liftx/core`'s absolute-timestamp timer math
 * (`restRemainingSeconds`/`isRestComplete`/`restProgress` are already fully
 * generic over `{startedAt, endsAt}` — nothing here is rest-specific — so
 * this reuses them directly rather than writing a second, incompatible
 * countdown calculation). Kept as a separate hook/file rather than widening
 * `useRestTimer` itself, since that would touch an already-shipped,
 * already-tested module for a rename with no behavioural benefit.
 */
export function useHoldTimer(timer: ActiveHoldTimer | null): HoldTimerDisplay {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (timer === null) return;

    let frame: number | null = null;

    function scheduleIfVisible(): void {
      if (document.visibilityState !== 'visible') return;
      frame = requestAnimationFrame(() => {
        setNow(Date.now());
        scheduleIfVisible();
      });
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        if (frame === null) scheduleIfVisible();
      } else if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    }

    setNow(Date.now());
    scheduleIfVisible();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [timer]);

  if (timer === null) return IDLE;

  const asRestTimer: RestTimer = {
    startedAt: timer.startedAt,
    endsAt: timer.endsAt,
    durationS: (timer.endsAt - timer.startedAt) / 1000,
  };

  return {
    remainingSeconds: restRemainingSeconds(asRestTimer, now),
    progress: restProgress(asRestTimer, now),
    isComplete: isRestComplete(asRestTimer, now),
  };
}
